import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Container, Row, Col, Card, Button, Badge, Accordion, 
  Form, Stack, Spinner, Modal, ListGroup, Tab, Nav, ProgressBar, Alert 
} from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api, { getConceptOutcomes, pushAssessmentToMoodle, pushAssessmentToClassroom, listMoodleCoursesForAssessment, listClassroomCoursesForAssessment } from '../../api/api';
import AppHeader from '../Common/AppHeader';
import { showToast } from '../../utils/toast';

// Robustly unwrap options regardless of how the API serializes them.
// Handles: plain array, array-wrapping-JSON-string, JSON string, double-stringified.
const parseOptions = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    if (raw.length === 1 && typeof raw[0] === 'string') {
      try { const inner = JSON.parse(raw[0]); if (Array.isArray(inner)) return inner; } catch (_) {}
    }
    return raw;
  }
  let value = raw;
  for (let i = 0; i < 5; i++) {
    if (Array.isArray(value)) {
      if (value.length === 1 && typeof value[0] === 'string') {
        try { const inner = JSON.parse(value[0]); if (Array.isArray(inner)) return inner; } catch (_) {}
      }
      return value;
    }
    if (typeof value !== 'string') break;
    try { value = JSON.parse(value); } catch (_) { break; }
  }
  return Array.isArray(value) ? value : [];
};

const AssessmentBankView = () => {
  const { slug, projectId } = useParams();
  const navigate = useNavigate();
  const tenantModules = useSelector((s) => s.user?.tenantModules || ["examination"]);

  // Gate: redirect users without the assessment_bank module
  useEffect(() => {
    if (!tenantModules.includes('assessment_bank')) {
      navigate('/dashboard', { replace: true });
    }
  }, [tenantModules, navigate]);

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [blueprint, setBlueprint] = useState(null);
  const [activeQ, setActiveQ] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  // Asset Management State
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Outcome mappings: { "<concept_id>": [{lo_code, lo_text, co_code, po_code, pso_code}] }
  const [conceptOutcomes, setConceptOutcomes] = useState({});

  // Moodle push state
  const [showMoodle, setShowMoodle] = useState(false);
  const [moodleCourses, setMoodleCourses] = useState([]);
  const [moodleCoursesLoading, setMoodleCoursesLoading] = useState(false);
  const [moodleCoursesError, setMoodleCoursesError] = useState('');
  const [moodleSelectedCourse, setMoodleSelectedCourse] = useState(null);
  const [moodleSearch, setMoodleSearch] = useState('');
  const [moodleDropdownOpen, setMoodleDropdownOpen] = useState(false);
  const [moodleQuizName, setMoodleQuizName] = useState('');
  const [moodleTimeLimitMin, setMoodleTimeLimitMin] = useState(0);
  const [moodleAttempts, setMoodleAttempts] = useState(1);
  const [moodlePushing, setMoodlePushing] = useState(false);
  const [moodleResult, setMoodleResult] = useState(null);
  const [moodleError, setMoodleError] = useState('');

  // Classroom push state
  const [showClassroom, setShowClassroom] = useState(false);
  const [classroomStep, setClassroomStep] = useState('auth'); // auth | courses | pushing | done | error
  const [classroomCourses, setClassroomCourses] = useState([]);
  const [classroomSelectedCourse, setClassroomSelectedCourse] = useState('');
  const [classroomAccessToken, setClassroomAccessToken] = useState('');
  const [classroomResult, setClassroomResult] = useState(null);
  const [classroomError, setClassroomError] = useState('');

  // --- 1. Load Data ---
  const fetchData = async () => {
    // 1. Extract the 'timestamp' (Batch ID) from the URL search parameters
    const queryParams = new URLSearchParams(window.location.search);
    const ts = queryParams.get('timestamp'); 

    try {
      const [qRes, bRes, outcomeRes] = await Promise.all([
        api.get(`/tenant/${slug}/projects/${projectId}/assessment-bank${ts ? `?timestamp=${ts}` : ''}`),
        api.get(`/tenant/${slug}/projects/${projectId}/full-profile`),
        getConceptOutcomes(slug, projectId).catch(() => ({ data: {} })),
      ]);

      setQuestions(qRes.data);
      setBlueprint(bRes.data);
      setConceptOutcomes(outcomeRes.data || {});

      if (qRes.data.length > 0 && !activeQ) {
        setActiveQ(qRes.data[0]);
      }
    } catch (err) {
      console.error("Fetch Error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // 4. Added window.location.search as a dependency to re-run when clicking different batches
  }, [projectId, window.location.search]);

  // --- 2. Coverage & QA Metrics ---
  const stats = useMemo(() => {
    const passed = questions.filter(q => q.qc_status === 'passed').length;
    const total = questions.length;
    return {
      total,
      passed,
      flagged: questions.filter(q => q.qc_status === 'flagged' || q.qc_status === 'pending').length,
      completion: total > 0 ? Math.round((passed / total) * 100) : 0
    };
  }, [questions]);

  // --- 3. SME Action Handlers ---
  const handleUpdateStatus = async (questionId, newStatus) => {
    try {
      await api.patch(`/tenant/${slug}/projects/${projectId}/questions/${questionId}/status`, {
        status: newStatus
      });
      // Optimized state update instead of full refresh
      setQuestions(prev => prev.map(q => q.id === questionId ? { ...q, qc_status: newStatus } : q));
      if (activeQ?.id === questionId) setActiveQ({ ...activeQ, qc_status: newStatus });
    } catch (err) {
      showToast("Status update failed.", 'error');
    }
  };

  // --- 4. Asset Upload (Handshake) ---
  const handleFileUpload = async () => {
    if (!selectedFile || !activeQ) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const res = await api.post(
        `/tenant/${slug}/projects/${projectId}/questions/${activeQ.id}/upload-asset`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Update local state to show optimized preview immediately
      const updatedQ = { 
        ...activeQ, 
        grounding_ref: { ...activeQ.grounding_ref, asset_url: res.data.s3_key },
        qc_status: 'passed' 
      };
      setQuestions(prev => prev.map(q => q.id === activeQ.id ? updatedQ : q));
      setActiveQ(updatedQ);
      setShowUpload(false);
      setSelectedFile(null);
    } catch (err) {
      showToast("Upload failed. Verify image format.", 'error');
    } finally {
      setUploading(false);
    }
  };

  const startEditing = () => {
    setEditData({ ...activeQ });
    setIsEditing(true);
  };

  // --- NEW: Manual QC Save Handler ---
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      // ✅ Use the PATCH endpoint we discussed for manual updates
      await api.patch(`/tenant/${slug}/projects/${projectId}/questions/${editData.id}`, editData);
      
      // Update local state
      setQuestions(prev => prev.map(q => q.id === editData.id ? { ...editData, qc_status: 'passed' } : q));
      setActiveQ({ ...editData, qc_status: 'passed' });
      setIsEditing(false); // Exit edit mode
      
      showToast("Question updated and approved successfully.", 'success');
    } catch (err) {
      console.error("Manual Edit Failed", err);
      showToast("Failed to save changes.", 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- 5. Professional Exports ---
  const handleExport = async (format) => {
    const queryParams = new URLSearchParams(window.location.search);
    const ts = queryParams.get('timestamp'); // This is the batch.id/timestamp
    try {
      const response = await api.get(
        `/tenant/${slug}/projects/${projectId}/export-assessment`, 
        {
          params: { 
            format, 
            timestamp: ts // ✅ Pass the batch timestamp to the exporter
          },
          responseType: 'blob'
        }
      ) ;
      const ext = format === 'gift' ? 'txt' : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `Assessment_Bank_${projectId}.${ext}`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
        showToast("Export failed. Ensure required sections are approved.", 'error');
    }
  };

  // ── Moodle push handlers ──────────────────────────────────────────────────
  const openMoodleModal = async () => {
    setShowMoodle(true);
    setMoodleResult(null);
    setMoodleError('');
    setMoodleSelectedCourse(null);
    setMoodleSearch('');
    setMoodleQuizName(blueprint?.name || '');
    if (moodleCourses.length === 0) {
      setMoodleCoursesLoading(true);
      setMoodleCoursesError('');
      try {
        const data = await listMoodleCoursesForAssessment(slug);
        setMoodleCourses(data.courses || []);
      } catch (e) {
        setMoodleCoursesError(e?.response?.data?.detail || 'Could not load Moodle courses. Configure Moodle in ExaminationOS Settings.');
      } finally {
        setMoodleCoursesLoading(false);
      }
    }
  };

  const handleMoodlePush = async () => {
    if (!moodleSelectedCourse) { setMoodleError('Please select a Moodle course.'); return; }
    setMoodlePushing(true);
    setMoodleError('');
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const ts = queryParams.get('timestamp');
      const res = await pushAssessmentToMoodle(slug, projectId, {
        course_id: moodleSelectedCourse.id,
        quiz_name: moodleQuizName || blueprint?.name || 'Assessment Bank',
        time_limit: parseInt(moodleTimeLimitMin) * 60 || null,
        attempts: parseInt(moodleAttempts) || 1,
        timestamp: ts || null,
      });
      setMoodleResult(res);
    } catch (e) {
      setMoodleError(e?.response?.data?.detail || 'Push to Moodle failed.');
    } finally {
      setMoodlePushing(false);
    }
  };

  // ── Classroom push handlers ───────────────────────────────────────────────
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const CLASSROOM_SCOPE = 'https://www.googleapis.com/auth/classroom.coursework.students https://www.googleapis.com/auth/forms https://www.googleapis.com/auth/classroom.courses.readonly';

  const openClassroomModal = () => {
    setShowClassroom(true);
    setClassroomStep('auth');
    setClassroomCourses([]);
    setClassroomSelectedCourse('');
    setClassroomAccessToken('');
    setClassroomResult(null);
    setClassroomError('');
  };

  const handleClassroomAuth = () => {
    if (!GOOGLE_CLIENT_ID) { setClassroomError('Google Client ID is not configured.'); setClassroomStep('error'); return; }
    const loadAndRequest = () => {
      const requestToken = () => {
        window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: CLASSROOM_SCOPE,
          callback: async (response) => {
            if (response.error) { setClassroomError(`Authorization failed: ${response.error}`); setClassroomStep('error'); return; }
            const token = response.access_token;
            setClassroomAccessToken(token);
            setClassroomStep('courses');
            try {
              const list = await listClassroomCoursesForAssessment(slug, projectId, token);
              setClassroomCourses(list);
            } catch { setClassroomError('Failed to fetch your Google Classroom courses.'); setClassroomStep('error'); }
          },
        }).requestAccessToken({ prompt: 'consent' });
      };
      if (window.google?.accounts?.oauth2) { requestToken(); return; }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = requestToken;
      script.onerror = () => { setClassroomError('Failed to load Google Identity Services.'); setClassroomStep('error'); };
      document.head.appendChild(script);
    };
    loadAndRequest();
  };

  const handleClassroomPush = async () => {
    if (!classroomSelectedCourse) return;
    setClassroomStep('pushing');
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const ts = queryParams.get('timestamp');
      const res = await pushAssessmentToClassroom(slug, projectId, {
        access_token: classroomAccessToken,
        course_id: classroomSelectedCourse,
        title: blueprint?.name || 'Assessment Bank',
        timestamp: ts || null,
      });
      setClassroomResult(res);
      setClassroomStep('done');
    } catch (err) {
      setClassroomError(err?.response?.data?.detail || 'Push to Google Classroom failed.');
      setClassroomStep('error');
    }
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" aria-hidden="true" /></div>;

  return (
    <div className="bg-light min-vh-100">
      <Container fluid className="py-4">
        {/* Top Command Bar */}
        <div className="d-flex justify-content-between align-items-center mb-4 px-3">
          <div>
          
            <Badge bg="dark" className="mb-2">SME REVIEW MODULE</Badge>
            <h2 className="fw-bold mb-0">{blueprint?.name} — Approved Bank</h2>
          </div>
          <Stack direction="horizontal" gap={2}>
	  <Button
              variant="link"
              className="text-decoration-none p-0 mb-2 text-muted small gap-2"
              onClick={() => navigate(`/tenant/${slug}/projects`)}
            >
              <Badge bg="primary" className="me-2 p-2"> View All Projects </Badge>
            </Button>
            <Button variant="outline-dark" size="sm" onClick={() => handleExport('docx')}>
              <i className="bi bi-file-earmark-word me-2"></i>Export DOCX
            </Button>
            {/* ✅ NEW: GIFT Export Button */}
            <Button variant="outline-dark" size="sm" onClick={() => handleExport('gift')}>
              <i className="bi bi-file-text me-2"></i>Export Moodle GIFT
            </Button>
            <Button variant="outline-primary" size="sm" onClick={openMoodleModal}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="me-1" style={{ verticalAlign: 'middle' }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" strokeWidth="1.6"/>
                <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
              Push to Moodle
            </Button>
            <Button variant="outline-success" size="sm" onClick={openClassroomModal}>
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" className="me-1" style={{ verticalAlign: 'middle' }}>
                <rect x="1" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                <circle cx="10" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
              </svg>
              Push to Classroom
            </Button>
            {/*<Button variant="outline-dark" size="sm" onClick={() => handleExport('pdf')}>
              <i className="bi bi-file-earmark-pdf me-2"></i>Export PDF
            </Button> */}
            <Button variant="primary" className="fw-bold px-4">
              <i className="bi bi-cloud-check me-2"></i>Save Final Draft
            </Button>
          </Stack>
        </div>

        <Row className="g-4">
          {/* LEFT: Interactive Question List */}
          <Col lg={8}>
            <div className="pe-2" style={{ maxHeight: '82vh', overflowY: 'auto', scrollbarWidth: 'thin' }}>
              {questions.map((q, idx) => (
                <Card 
                  key={q.id} 
                  className={`mb-3 border-0 shadow-sm transition-all ${activeQ?.id === q.id ? 'ring-primary border-start border-4 border-primary' : ''}`}
                  style={{ cursor: isEditing ? 'not-allowed' : 'pointer' }}
                  onClick={() => !isEditing && setActiveQ(q)}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <Badge bg="secondary" className="me-2 text-uppercase">Q{idx + 1} • {q.type}</Badge>
                        <Badge bg="light" text="dark" className="border">CO: {q.co_mapping}</Badge>
                      </div>
                      <Badge bg={q.qc_status === 'passed' ? 'success' : 'warning'}>
                        {q.qc_status === 'passed' ? '✓ APPROVED' : '⚠ REVIEW REQUIRED'}
                      </Badge>
                    </div>
                    
                    {/* LaTeX & Markdown Rendering */}
                    {/* Updated logic inside the questions.map loop */}
                    <div className="p-3 bg-white rounded border mb-3">
                      {/* ✅ FIX: Only show the editor if this specific question is being edited */}
                      {isEditing && editData.id === q.id ? (
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-primary">MANUAL EDIT MODE</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            className="border-primary shadow-sm"
                            value={editData.question_text}
                            onChange={(e) => setEditData({ ...editData, question_text: e.target.value })}
                          />
                          <Form.Text className="text-muted small">
                            Changes will be saved to the primary Assessment Bank upon submission.
                          </Form.Text>
                        </Form.Group>
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                          {q.question_text}
                        </ReactMarkdown>
                      )}
                    </div>
                    {/* MCQ Options — shown for mcq type questions only */}
                    {q.type?.toLowerCase().split('.').pop() === 'mcq' && (() => {
                      const opts = parseOptions(q.options);
                      if (!opts.length) return null;
                      
                      // 🌟 ROBUST ANSWER KEY PARSER
                      let correctIdx = -1;
                      const rawAns = (q.answer_key || q.answer || '').trim();

                      // Strategy 1: Check if it starts with A, B, C, D, E (e.g., "A", "A)", "A. text")
                      const letterMatch = rawAns.match(/^([a-eA-E])(?:[\.\)]|\s|$)/);
                      if (letterMatch) {
                        // Convert character to array index (A=0, B=1, C=2...)
                        correctIdx = letterMatch[1].toUpperCase().charCodeAt(0) - 65;
                      }

                      // Strategy 2: If no letter prefix, attempt fuzzy text matching against the options
                      if (correctIdx === -1 && rawAns.length > 1) {
                        const cleanAns = rawAns.toLowerCase().trim();
                        correctIdx = opts.findIndex(opt => {
                          const cleanOpt = opt.replace(/^[a-eA-E][\.\)]\s*/, '').toLowerCase().trim();
                          return cleanOpt === cleanAns || cleanOpt.includes(cleanAns) || cleanAns.includes(cleanOpt);
                        });
                      }
                      return (
                        <div className="mb-3">
                          <div className="small fw-bold text-muted text-uppercase mb-2 px-1">Answer Options</div>
                          <ListGroup variant="flush" className="border rounded">
                            {opts.map((opt, i) => {
                              const isCorrect = i === correctIdx;
                              // Strip leading "A) " / "A. " prefix if present
                              const optText = opt.replace(/^[A-E][\.\)]\s*/i, '');
                              const letter = String.fromCharCode(65 + i);
                              return (
                                <ListGroup.Item
                                  key={i}
                                  className={`d-flex align-items-center gap-2 py-2 px-3 small ${
                                    isCorrect
                                      ? 'bg-success bg-opacity-10 border-start border-3 border-success'
                                      : 'bg-white'
                                  }`}
                                >
                                  <Badge
                                    bg={isCorrect ? 'success' : 'light'}
                                    text={isCorrect ? 'white' : 'dark'}
                                    className="border flex-shrink-0"
                                    style={{ minWidth: '26px' }}
                                  >
                                    {letter}
                                  </Badge>
                                  <span className={isCorrect ? 'fw-semibold text-success' : 'text-dark'}>
                                    {optText}
                                  </span>
                                  {isCorrect && (
                                    <i className="bi bi-check-circle-fill text-success ms-auto flex-shrink-0"></i>
                                  )}
                                </ListGroup.Item>
                              );
                            })}
                          </ListGroup>
                        </div>
                      );
                    })()}

                    {/* Inline Asset Handling */}
                    {(q.question_text.includes('[DIAGRAM_FACULTY]') || q.grounding_ref?.asset_url) && (
                      <div className="mb-3 p-3 bg-light rounded border border-dashed text-center">
                        {q.grounding_ref?.asset_url ? (
                          <div className="position-relative d-inline-block">
                             {/* Proxy URL handling consistent with main.py */}
                            <img 
                              src={`/api/university-econtent-engine-prod/${q.grounding_ref.asset_url}`} 
                              alt="SME Asset" 
                              className="img-fluid rounded shadow-sm"
                              style={{ maxHeight: '250px' }} 
                            />
                            <Button size="sm" variant="dark" className="position-absolute bottom-0 end-0 m-2" onClick={() => setShowUpload(true)}>Replace</Button>
                          </div>
                        ) : (
                          <div className="py-4">
                            <i className="bi bi-image text-muted display-6"></i>
                            <p className="small text-muted mt-2">Faculty diagram required for this question.</p>
                            <Button size="sm" variant="primary" onClick={() => setShowUpload(true)}>Upload Required Diagram</Button>
                          </div>
                        )}
                      </div>
                    )}

                    <Accordion flush className="border rounded">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header className="small fw-bold py-2">Marking Rubric & Academic Logic</Accordion.Header>
                        <Accordion.Body className="bg-light small">
                          <Row>
                            <Col md={7}>
                              <div className="fw-bold text-primary mb-2">Stepwise Explanation:</div>
                              <div className="text-muted">{q.explanation}</div>
                            </Col>
                            <Col md={5} className="border-start">
                              <div className="fw-bold text-success mb-2">Marking Scheme:</div>
                              <ListGroup variant="flush" className="bg-transparent">
                                {q.rubric?.map((r, i) => (
                                  <ListGroup.Item key={i} className="bg-transparent px-0 py-1 d-flex justify-content-between">
                                    <span>{r.criterion}</span>
                                    <Badge bg="white" text="dark" className="border">{r.marks}M</Badge>
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Col>

          {/* RIGHT: SME Review Sidebar */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
              <Card.Header className="bg-white fw-bold py-3">Governance Dashboard</Card.Header>
              <Card.Body>
                <div className="mb-4">
                  <div className="d-flex justify-content-between small fw-bold mb-2">
                    <span>BANK READINESS</span>
                    <span>{stats.passed} / {stats.total}</span>
                  </div>
                  <ProgressBar now={stats.completion} variant={stats.completion === 100 ? 'success' : 'primary'} className="mb-2" style={{ height: '10px' }} />
                  <div className="small text-muted">Approved questions are locked for final export.</div>
                </div>

                <hr />

                {activeQ ? (
                  <Stack gap={3}>
                    {/* ── Item Intelligence ── */}
                    <div>
                      <h6 className="small fw-bold text-muted text-uppercase mb-2">Item Intelligence</h6>
                      <div className="p-3 bg-light rounded border small">
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                          <strong>Cognitive Level:</strong>
                          <Badge bg="info">{activeQ.bloom_level}</Badge>
                        </div>
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                          <strong>Course Outcome (CO):</strong>
                          <Badge bg="dark">{activeQ.co_mapping || '—'}</Badge>
                        </div>
                        {/* ── LO / PO / PSO from outcome_mappings ── */}
                        {(() => {
                          // Collect concept IDs from this question's concept_mapping
                          const cids = Array.isArray(activeQ.concept_mapping)
                            ? activeQ.concept_mapping.map(c => String(typeof c === 'object' ? c.concept_id : c)).filter(Boolean)
                            : [];
                          // Gather unique LO/PO/PSO from outcome_mappings for those concepts
                          const los = new Map(), pos = new Set(), psos = new Set();
                          cids.forEach(cid => {
                            (conceptOutcomes[cid] || []).forEach(o => {
                              if (o.lo_code) los.set(o.lo_code, o.lo_text || o.lo_code);
                              if (o.po_code)  pos.add(o.po_code);
                              if (o.pso_code) psos.add(o.pso_code);
                            });
                          });
                          if (!los.size && !pos.size && !psos.size) return null;
                          return (
                            <>
                              {los.size > 0 && (
                                <div className="mb-2">
                                  <strong className="d-block mb-1" style={{ fontSize: '0.75rem' }}>Learning Outcomes (LO):</strong>
                                  <div className="d-flex flex-wrap gap-1">
                                    {[...los.entries()].map(([code, text]) => (
                                      <Badge key={code} bg="secondary" title={text} style={{ cursor: 'help', fontSize: '0.65rem' }}>
                                        {code}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {pos.size > 0 && (
                                <div className="mb-2 d-flex justify-content-between align-items-center">
                                  <strong>Programme Outcomes (PO):</strong>
                                  <span className="text-muted small">{[...pos].join(', ')}</span>
                                </div>
                              )}
                              {psos.size > 0 && (
                                <div className="mb-2 d-flex justify-content-between align-items-center">
                                  <strong>PSOs:</strong>
                                  <span className="text-muted small">{[...psos].join(', ')}</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                          <strong>Difficulty Score:</strong>
                          {(() => {
                            // Extract numeric 1-5 score from grounding_ref or concept_mapping.
                            // assessment_bank.difficulty stores a category label ("Undergraduate"),
                            // not the actual numeric score.
                            const score =
                              activeQ.grounding_ref?.concepts?.[0]?.difficulty_score ??
                              (Array.isArray(activeQ.concept_mapping) && activeQ.concept_mapping[0]?.difficulty_score);
                            if (score == null) return <span className="text-muted small">—</span>;
                            const label  = score <= 1.5 ? 'Easy' : score <= 3 ? 'Moderate' : score <= 4 ? 'Hard' : 'Very Hard';
                            const colour = score <= 1.5 ? 'success' : score <= 3 ? 'warning' : 'danger';
                            return (
                              <Badge bg={colour}>
                                {Number(score).toFixed(1)}/5 &mdash; {label}
                              </Badge>
                            );
                          })()}
                        </div>
                        {activeQ.grounding_ref?.confidence != null && (
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>Grounding Confidence:</strong>
                            <Badge bg={activeQ.grounding_ref.confidence >= 0.8 ? 'success' : activeQ.grounding_ref.confidence >= 0.5 ? 'warning' : 'danger'}>
                              {Math.round(activeQ.grounding_ref.confidence * 100)}%
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Textbook Grounding ── */}
                    <div>
                      <h6 className="small fw-bold text-muted text-uppercase mb-2">
                        <i className="bi bi-book me-1"></i>Textbook Grounding
                      </h6>
                      <div className="p-3 bg-white rounded border small">
                        {activeQ.grounding_ref?.source_text ? (
                          <div className="d-flex gap-2 align-items-start">
                            <i className="bi bi-pin-map-fill text-primary flex-shrink-0 mt-1"></i>
                            <span className="text-dark fw-semibold">{activeQ.grounding_ref.source_text}</span>
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">No source reference recorded</span>
                        )}

                        {/* grounding_ref.concepts — source paragraph anchors per concept */}
                        {activeQ.grounding_ref?.concepts?.length > 0 && (
                          <div className="mt-3">
                            <div className="text-muted fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                              SOURCE ANCHORS
                            </div>
                            {activeQ.grounding_ref.concepts.map((gc, i) => {
                              const sm = gc.source_mapping
                                ? (typeof gc.source_mapping === 'string'
                                    ? (() => { try { return JSON.parse(gc.source_mapping); } catch { return {}; } })()
                                    : gc.source_mapping)
                                : {};
                              const sources = sm?.sources || [];
                              const paraIds = sources[0]?.paragraph_ids || [];
                              const pageRange = sources[0]?.page_range;
                              const confidence = sources[0]?.confidence;
                              return (
                                <div key={i} className="mb-2 p-2 bg-light rounded border" style={{ fontSize: '0.75rem' }}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="fw-semibold text-primary">{gc.concept_name}</span>
                                    {confidence != null && (
                                      <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                                        {Math.round(confidence * 100)}% conf
                                      </Badge>
                                    )}
                                  </div>
                                  {pageRange && (
                                    <div className="text-muted">
                                      <i className="bi bi-file-earmark-text me-1"></i>
                                      Pages {pageRange[0]}–{pageRange[1]}
                                    </div>
                                  )}
                                  {paraIds.length > 0 && (
                                    <div className="text-muted mt-1">
                                      <i className="bi bi-paragraph me-1"></i>
                                      Paragraphs: {paraIds.slice(0, 4).join(', ')}{paraIds.length > 4 ? ` +${paraIds.length - 4} more` : ''}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Concept Mapping ── */}
                    {activeQ.concept_mapping?.length > 0 && (
                      <div>
                        <h6 className="small fw-bold text-muted text-uppercase mb-2">
                          <i className="bi bi-diagram-3 me-1"></i>Concept Mapping
                        </h6>
                        <div className="d-flex flex-column gap-2">
                          {activeQ.concept_mapping.map((cm, i) => (
                            <div key={i} className="p-2 bg-white rounded border small">
                              <div className="d-flex justify-content-between align-items-start gap-1 mb-1">
                                <span className="fw-semibold text-dark" style={{ lineHeight: 1.3 }}>
                                  {cm.concept_name}
                                </span>
                                <Badge bg="info" className="flex-shrink-0" style={{ fontSize: '0.65rem' }}>
                                  {cm.expected_bloom || cm.bloom_level || '—'}
                                </Badge>
                              </div>
                              {cm.topic_title && (
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                  <i className="bi bi-folder2 me-1"></i>{cm.topic_title}
                                </div>
                              )}
                              {cm.difficulty_score != null && (
                                <div className="mt-1 d-flex align-items-center gap-1">
                                  <span className="text-muted" style={{ fontSize: '0.7rem' }}>Difficulty:</span>
                                  <div className="flex-grow-1 bg-secondary bg-opacity-25 rounded" style={{ height: '4px' }}>
                                    <div
                                      className="bg-primary rounded"
                                      style={{ width: `${Math.min(100, (cm.difficulty_score / 5) * 100)}%`, height: '4px' }}
                                    />
                                  </div>
                                  <span className="text-muted" style={{ fontSize: '0.7rem' }}>{cm.difficulty_score}/5</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <h6 className="small fw-bold text-muted text-uppercase mb-2">Automated QC Report</h6>
                      {activeQ.qc_report?.reasons?.length > 0 ? (
                        <Alert variant="warning" className="small py-2 border-0 shadow-none" role="alert">
                          <ul className="mb-0 ps-3">
                            {activeQ.qc_report.reasons.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        </Alert>
                      ) : (
                        <Alert variant="success" className="small py-2 border-0" role="status">
                          <i className="bi bi-check-circle-fill me-2"></i>Grounded & Compliant
                        </Alert>
                      )}
                    </div>

                    <div className="pt-3 border-top">
                      {isEditing ? (
                        <Stack gap={2}>
                          <Button variant="primary" onClick={handleSaveEdit}>
                            <i className="bi bi-save me-2"></i>Save Changes
                          </Button>
                          <Button variant="outline-secondary" onClick={() => setIsEditing(false)}>
                            Cancel
                          </Button>
                        </Stack>
                      ) : (
                        <Button variant="outline-primary" className="w-100" onClick={startEditing}>
                          <i className="bi bi-pencil me-2"></i>Manual Edit
                        </Button>
                      )}
                      <Button 
                        variant="success" 
                        className="w-100 mb-2 py-2 fw-bold shadow-sm"
                        disabled={activeQ.qc_status === 'passed'}
                        onClick={() => handleUpdateStatus(activeQ.id, 'passed')}
                      >
                        <i className="bi bi-check-lg me-2"></i>Approve Question
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        className="w-100 py-2 fw-bold"
                        onClick={() => handleUpdateStatus(activeQ.id, 'flagged')}
                      >
                        <i className="bi bi-flag me-2"></i>Flag for Modification
                      </Button>
                    </div>
                  </Stack>
                ) : (
                  <div className="text-center py-5 text-muted small">Select a question to begin review.</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

{/* ✅ FIXED: Re-added the Modal Wrapper and Header tags */}
      <Modal show={showUpload} onHide={() => setShowUpload(false)} centered>
        <Modal.Header closeButton className="bg-light border-0">
          <Modal.Title className="h6 fw-bold">Attach Faculty Illustration</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form.Group>
            <Form.Label className="small fw-bold">Select Optimized File (JPG/PNG)</Form.Label>
            <Form.Control 
              type="file" 
              accept="image/*"
              onChange={(e) => setSelectedFile(e.target.files[0])} 
            />
            {/* ✅ KEEP: Preview Thumbnail Logic */}
            {selectedFile && (
              <div className="mt-3 text-center border rounded p-2 bg-light">
                <img 
                  src={URL.createObjectURL(selectedFile)} 
                  alt="Preview" 
                  style={{ maxHeight: '150px', maxWidth: '100%' }} 
                />
                <div className="small text-muted mt-1">{selectedFile.name}</div>
              </div>
            )}
            <Form.Text className="text-muted small">
              SME must provide the source diagram for grounding.
            </Form.Text>
          </Form.Group>
          {uploading && <ProgressBar animated now={100} className="mt-3" style={{ height: '5px' }} />}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={() => setShowUpload(false)}>Cancel</Button>
          <Button 
            variant="primary" 
            className="fw-bold px-4" 
            onClick={handleFileUpload} 
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Processing...' : 'Upload & Approve'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Push to Moodle Modal ─────────────────────────────────────────── */}
      <Modal show={showMoodle} onHide={() => setShowMoodle(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light border-0">
          <Modal.Title className="h6 fw-bold">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="me-2" style={{ verticalAlign: 'middle' }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" strokeWidth="1.6"/>
              <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            Push to Moodle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {moodleResult ? (
            <Alert variant="success">
              <div className="fw-bold mb-2">✓ Pushed successfully!</div>
              <div className="small">
                <div>Course: <strong>{moodleSelectedCourse?.fullname}</strong></div>
                <div>Quiz ID: <strong>{moodleResult.quiz_id}</strong></div>
                <div>Questions pushed: <strong>{moodleResult.questions_pushed}</strong></div>
                {moodleResult.quiz_url && (
                  <div className="mt-2">
                    <a href={moodleResult.quiz_url} target="_blank" rel="noreferrer" className="fw-semibold">
                      Open quiz in Moodle →
                    </a>
                  </div>
                )}
              </div>
            </Alert>
          ) : (
            <div className="d-flex flex-column gap-3">
              {moodleError && <Alert variant="danger" className="py-2 small">{moodleError}</Alert>}

              <Form.Group>
                <Form.Label className="small fw-bold text-uppercase text-muted">Moodle Course *</Form.Label>
                {moodleCoursesLoading ? (
                  <div className="d-flex align-items-center gap-2 py-2 small text-muted">
                    <Spinner animation="border" size="sm" /> Loading courses from Moodle…
                  </div>
                ) : moodleCoursesError ? (
                  <Alert variant="warning" className="py-2 small">{moodleCoursesError}</Alert>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      placeholder="Search course by name…"
                      value={moodleSearch}
                      onChange={(e) => { setMoodleSearch(e.target.value); setMoodleSelectedCourse(null); setMoodleDropdownOpen(true); }}
                      onFocus={() => setMoodleDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setMoodleDropdownOpen(false), 200)}
                    />
                    {moodleDropdownOpen && (
                      <div style={{ position: 'absolute', zIndex: 300, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 220, overflowY: 'auto' }}>
                        {(moodleCourses.filter(c => !moodleSearch || c.fullname.toLowerCase().includes(moodleSearch.toLowerCase()))).length === 0 ? (
                          <div className="p-3 text-muted small text-center">No courses found</div>
                        ) : moodleCourses.filter(c => !moodleSearch || c.fullname.toLowerCase().includes(moodleSearch.toLowerCase())).map((c) => (
                          <div
                            key={c.id}
                            onMouseDown={() => { setMoodleSelectedCourse(c); setMoodleSearch(c.fullname); setMoodleDropdownOpen(false); }}
                            className="p-2 px-3 small"
                            style={{ cursor: 'pointer', borderBottom: '1px solid #f0f0f0', background: moodleSelectedCourse?.id === c.id ? '#e8f0fe' : 'transparent' }}
                          >
                            <div className="fw-semibold">{c.fullname}</div>
                            {c.shortname && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{c.shortname}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>

              <Form.Group>
                <Form.Label className="small fw-bold text-uppercase text-muted">Quiz Name</Form.Label>
                <Form.Control
                  type="text"
                  value={moodleQuizName}
                  onChange={(e) => setMoodleQuizName(e.target.value)}
                  placeholder="Quiz name in Moodle"
                />
              </Form.Group>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Time Limit (minutes, 0 = no limit)</Form.Label>
                    <Form.Control type="number" min="0" value={moodleTimeLimitMin} onChange={(e) => setMoodleTimeLimitMin(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Max Attempts</Form.Label>
                    <Form.Control type="number" min="1" value={moodleAttempts} onChange={(e) => setMoodleAttempts(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="info" className="py-2 small mb-0">
                Only <strong>approved</strong> questions will be pushed. Export Moodle GIFT first to preview the questions.
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={() => setShowMoodle(false)}>
            {moodleResult ? 'Close' : 'Cancel'}
          </Button>
          {!moodleResult && (
            <Button variant="primary" className="fw-bold px-4" onClick={handleMoodlePush} disabled={moodlePushing || !moodleSelectedCourse}>
              {moodlePushing ? <><Spinner animation="border" size="sm" className="me-2" />Pushing…</> : 'Push to Moodle'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ── Push to Google Classroom Modal ───────────────────────────────── */}
      <Modal show={showClassroom} onHide={() => setShowClassroom(false)} centered>
        <Modal.Header closeButton className="bg-light border-0">
          <Modal.Title className="h6 fw-bold">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="me-2" style={{ verticalAlign: 'middle' }}>
              <rect x="1" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/>
              <circle cx="10" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
            </svg>
            Push to Google Classroom
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {classroomStep === 'auth' && (
            <div className="text-center py-4">
              <div style={{ fontSize: 40, marginBottom: 12 }}>🏫</div>
              <p className="small text-muted mb-4" style={{ lineHeight: 1.6 }}>
                This will create a Google Form quiz from <strong>{blueprint?.name || 'the assessment bank'}</strong> (approved questions only)
                and attach it as a draft assignment in your Google Classroom course.
              </p>
              <Button variant="primary" className="fw-bold" onClick={handleClassroomAuth}>
                Sign in with Google
              </Button>
            </div>
          )}

          {classroomStep === 'courses' && (
            <Form.Group>
              <Form.Label className="small fw-bold">Select Google Classroom Course *</Form.Label>
              {classroomCourses.length === 0 ? (
                <p className="small text-muted">No active courses found in your Google Classroom account.</p>
              ) : (
                <Form.Select value={classroomSelectedCourse} onChange={(e) => setClassroomSelectedCourse(e.target.value)}>
                  <option value="">— Select a course —</option>
                  {classroomCourses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.section ? ` · ${c.section}` : ''}</option>
                  ))}
                </Form.Select>
              )}
              <Alert variant="info" className="py-2 small mt-3 mb-0">
                The assignment will be created as a <strong>draft</strong> — review it before publishing to students.
              </Alert>
            </Form.Group>
          )}

          {classroomStep === 'pushing' && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="success" />
              <p className="small text-muted mt-3">Creating Google Form and attaching to Classroom…</p>
            </div>
          )}

          {classroomStep === 'done' && classroomResult && (
            <Alert variant="success">
              <div className="fw-bold mb-2">✓ Pushed to Google Classroom!</div>
              <div className="small">
                {classroomResult.form_url && (
                  <div className="mb-1"><a href={classroomResult.form_url} target="_blank" rel="noreferrer" className="fw-semibold">Open Google Form →</a></div>
                )}
                {classroomResult.assignment_url && (
                  <div><a href={classroomResult.assignment_url} target="_blank" rel="noreferrer" className="fw-semibold">Open Classroom Assignment →</a></div>
                )}
              </div>
            </Alert>
          )}

          {classroomStep === 'error' && (
            <Alert variant="danger" className="small">{classroomError}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={() => setShowClassroom(false)}>
            {classroomStep === 'done' ? 'Close' : 'Cancel'}
          </Button>
          {classroomStep === 'courses' && (
            <Button variant="success" className="fw-bold" onClick={handleClassroomPush} disabled={!classroomSelectedCourse}>
              Push to Classroom
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssessmentBankView;